/**
 * altHeld — a module-level "is the ⌥ Alt key currently held?" store.
 *
 * The cross-tab drag gesture is ⌥Alt+drag via NATIVE HTML5 DnD, and native
 * `dragstart` only fires from elements that are `draggable` at mousedown —
 * so the attribute must flip ON while Alt is held, BEFORE any drag begins.
 * One pair of window listeners feeds every grid item through
 * `useSyncExternalStore` (items re-render only on the Alt transition).
 *
 * `blur` clears the flag: Cmd-Tabbing away with Alt down would otherwise
 * strand `draggable=true` and break text selection on return.
 */
import { useSyncExternalStore } from "react";

let held = false;
const listeners = new Set<() => void>();

function set(next: boolean): void {
  if (held === next) return;
  held = next;
  for (const l of listeners) l();
}

function ensureWired(): void {
  if (typeof window === "undefined") return;
  if ((window as { __wbAltWired?: boolean }).__wbAltWired) return;
  (window as { __wbAltWired?: boolean }).__wbAltWired = true;
  window.addEventListener("keydown", (e) => {
    if (e.key === "Alt") set(true);
  });
  window.addEventListener("keyup", (e) => {
    if (e.key === "Alt") set(false);
  });
  window.addEventListener("blur", () => set(false));
}

export function isAltHeld(): boolean {
  return held;
}

function subscribe(cb: () => void): () => void {
  ensureWired();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** True while ⌥ Alt is held — flips grid items into native-drag mode. */
export function useAltHeld(): boolean {
  return useSyncExternalStore(subscribe, isAltHeld, () => false);
}
